import { Box, Button } from "@mui/material";
import { alpha, styled, Theme } from "@mui/material/styles";

type ContainerProps = {
  isAI: boolean;
  messageIndex: number;
};

export const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI" && prop !== "messageIndex"
})(({ isAI, messageIndex }: ContainerProps) => ({
  display: "flex",
  flexDirection: isAI ? "row" : "row-reverse",
  alignItems: "end",
  width: "100%",
  paddingLeft: "5px",
  paddingRight: "5px",
  marginTop: messageIndex === 0 ? "auto" : 0
}));

type AvatarContainerProps = {
  theme: Theme;
  isAI: boolean;
};

export const AvatarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI"
})(({ theme, isAI }: AvatarContainerProps) => ({
  borderRadius: 999,
  flexShrink: 0,
  border: `1px solid ${isAI ? theme.customColors.accentVariantB : "transparent"}`,
  marginBottom: "5px",
  width: "37px",
  height: "37px"
}));

type MessageContainerProps = {
  theme: Theme;
  isAI: boolean;
};

export const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isAI"
})(({ theme, isAI }: MessageContainerProps) => ({
  backgroundColor: theme.customColors.backgroundPrimary,
  borderRadius: "10px",
  borderLeft: `2px solid ${isAI ? theme.customColors.accentVariantB : "transparent"}`,
  borderRight: `2px solid ${isAI ? "transparent" : theme.customColors.accentVariantB}`,
  textAlign: `${isAI ? "left" : "right"}`,
  paddingLeft: "30px",
  paddingRight: "30px",
  paddingTop: "10px",
  paddingBottom: "10px",
  maxWidth: "80%",
  display: "flex"
}));

export const MessageBox = styled(Box)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "14px",
  animation: "blink 0.5s steps(2, start) infinite",
  "@keyframes blink": {
    "50%": { borderRightColor: "transparent" }
  },
  "> p": {
    margin: 0
  }
}));

export const SuggestedQuestionsRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: "8px",
  width: "100%",
  marginTop: "10px",
  paddingLeft: "47px"
}));

type SuggestedQuestionChipProps = {
  chipIndex: number;
};

export const SuggestedQuestionChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== "chipIndex"
})(({ theme, chipIndex }: { theme: Theme } & SuggestedQuestionChipProps) => ({
  textTransform: "none",
  textAlign: "left",
  justifyContent: "flex-start",
  fontSize: "13px",
  lineHeight: 1.3,
  color: theme.customColors.textPrimary,
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.accentVariantB}`,
  borderRadius: "10px",
  padding: "8px 14px",
  maxWidth: "80%",
  opacity: 0,
  animation: "suggestedChipFadeIn 0.35s ease-out forwards",
  animationDelay: `${chipIndex * 0.1}s`,
  "@keyframes suggestedChipFadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(8px)"
    },
    to: {
      opacity: 1,
      transform: "translateY(0)"
    }
  },
  "& .MuiButton-startIcon": {
    color: theme.customColors.accentVariantB
  },
  "&:hover": {
    backgroundColor: alpha(theme.customColors.accentVariantB, 0.1)
  },
  "&.Mui-disabled": {
    color: theme.customColors.textPrimary,
    borderColor: alpha(theme.customColors.accentVariantB, 0.5),
    "& .MuiButton-startIcon": {
      color: alpha(theme.customColors.accentVariantB, 0.6)
    }
  }
}));
